import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

function StatusBadge({ status, days_left }) {
  if (status === "pending")
    return <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm badge-unpaid">PENDING</span>;
  if (status === "expired" || (days_left != null && days_left < 0))
    return <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm badge-overdue">EXPIRED</span>;
  if (days_left != null && days_left <= 3)
    return <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm badge-due-today">EXPIRING</span>;
  return <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-sm badge-paid">ACTIVE</span>;
}

export default function AdminSubscriptions() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [extendOpen, setExtendOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [mode, setMode] = useState("days"); // 'days' | 'date'
  const [days, setDays] = useState(30);
  const [expireDate, setExpireDate] = useState("");

  const load = async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (search) params.search = search;
    const { data } = await api.get("/admin/subscriptions", { params });
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, search]);

  const openExtend = (s) => {
    setTarget(s);
    setMode("days");
    setDays(30);
    setExpireDate(s.expire_date ? s.expire_date.slice(0, 10) : new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
    setExtendOpen(true);
  };

  const extend = async () => {
    try {
      const payload = mode === "date" ? { expire_date: expireDate } : { days: parseInt(days) || 30 };
      await api.post(`/admin/subscriptions/${target.id}/extend`, payload);
      toast.success(mode === "date" ? `Expire date set to ${expireDate}` : `Extended by ${days} days`);
      setExtendOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this subscription?")) return;
    try { await api.delete(`/admin/subscriptions/${id}`); toast.success("Deleted"); load(); }
    catch (_) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Subscriptions</h1>
        <p className="text-sm text-muted-foreground mt-1">{rows.length} total.</p>
      </div>

      <div className="glass-card rounded-lg p-4 flex flex-wrap gap-2 items-center">
        <Input
          data-testid="sub-search"
          placeholder="Search by user or plan…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[180px]"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="sub-status-filter" className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="glass-card rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Shared</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead>Days Left</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No subscriptions.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((s) => {
                const overdue = s.days_left != null && s.days_left < 0;
                return (
                  <TableRow
                    key={s.id}
                    data-testid={`sub-${s.id}`}
                    className={overdue ? "bg-red-500/[0.04]" : ""}
                  >
                    <TableCell>
                      <div className="font-medium">{s.user?.name || "—"}</div>
                      <div className="text-xs font-mono text-muted-foreground">{s.user?.email}</div>
                    </TableCell>
                    <TableCell>{s.plan?.name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{s.shared_account?.account_name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{s.expire_date?.slice(0, 10) || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {s.days_left == null ? "—" : s.days_left < 0 ? `${-s.days_left}d overdue` : `${s.days_left}d`}
                    </TableCell>
                    <TableCell><StatusBadge status={s.status} days_left={s.days_left} /></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => openExtend(s)} data-testid={`extend-${s.id}`}>
                        Extend
                      </Button>
                      <Button size="icon" variant="ghost" className="text-red-500 ml-1" onClick={() => del(s.id)} data-testid={`del-sub-${s.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={extendOpen} onOpenChange={setExtendOpen}>
        <DialogContent className="sm:max-w-md" data-testid="extend-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Extend Subscription</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              User: <span className="font-medium text-foreground">{target?.user?.name}</span>
              {target?.expire_date && (
                <>
                  <br />
                  Current expiry:{" "}
                  <span className="font-mono text-foreground">{target.expire_date.slice(0, 10)}</span>
                </>
              )}
            </p>
            <Tabs value={mode} onValueChange={setMode}>
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="days" data-testid="extend-mode-days">Add Days</TabsTrigger>
                <TabsTrigger value="date" data-testid="extend-mode-date">Set Date</TabsTrigger>
              </TabsList>
              <TabsContent value="days" className="mt-3">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Days to add (from current expiry)
                </label>
                <Input
                  data-testid="extend-days"
                  type="number"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  className="mt-1.5"
                />
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {[7, 15, 30, 60, 90].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDays(d)}
                      data-testid={`days-preset-${d}`}
                      className="text-xs px-2 py-1 rounded-sm border border-border hover:bg-accent"
                    >
                      +{d}d
                    </button>
                  ))}
                </div>
              </TabsContent>
              <TabsContent value="date" className="mt-3">
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  New expire date
                </label>
                <Input
                  data-testid="extend-date"
                  type="date"
                  value={expireDate}
                  onChange={(e) => setExpireDate(e.target.value)}
                  className="mt-1.5"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Useful for offline payments or manual adjustments.
                </p>
              </TabsContent>
            </Tabs>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendOpen(false)} data-testid="extend-cancel">Cancel</Button>
            <Button onClick={extend} data-testid="extend-confirm">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
